import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import './EnrichmentEditor.css';

interface EnrichmentEditorProps {
  entityType: 'venue' | 'event' | 'artist';
  entityId: string;
  enrichmentType: 'ticketmaster' | 'spotify' | 'etix' | 'eventim';
  onClose: () => void;
  entityName?: string;
}

const EnrichmentEditor: React.FC<EnrichmentEditorProps> = ({
  entityType,
  entityId,
  enrichmentType,
  onClose,
  entityName,
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<any>({});
  const [originalData, setOriginalData] = useState<any>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [loadingSpotify, setLoadingSpotify] = useState(false);

  const loadEnrichmentData = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const endpoint = `/admin/${entityType}s/${entityId}/${enrichmentType}`;
      const response = await apiRequest<any>(endpoint, token);
      setFormData(response);
      setOriginalData(JSON.parse(JSON.stringify(response || {}))); // Deep copy for comparison
      setError('');
    } catch (err: any) {
      if (err.message.includes('404')) {
        // No enrichment data exists yet - start with empty form
        setFormData({});
        setOriginalData({});
      } else {
        setError(err.message || 'Failed to load enrichment data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && entityId) {
      loadEnrichmentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, entityId]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Check for unsaved changes using current state
        const hasChanges = originalData && JSON.stringify(formData) !== JSON.stringify(originalData);
        if (hasChanges) {
          setShowUnsavedWarning(true);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [formData, originalData, onClose]);

  const handleSave = async () => {
    if (!token) return;
    try {
      setSaving(true);
      const endpoint = `/admin/${entityType}s/${entityId}/${enrichmentType}`;
      await apiRequest(endpoint, token, {
        method: 'PUT',
        body: JSON.stringify(formData),
      });
      await loadEnrichmentData();
      setError('');
      setIframeKey(prev => prev + 1); // Refresh iframe after save
    } catch (err: any) {
      setError(err.message || 'Failed to save enrichment data');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path: string[], value: any) => {
    const newData = { ...formData };
    let current = newData;
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
    setFormData(newData);
  };

  const hasUnsavedChanges = () => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    onClose();
  };

  const handleRefreshIframe = () => {
    setIframeKey(prev => prev + 1);
  };

  const getSpotifyId = () => {
    return formData?.spotify_id || '';
  };

  const shouldShowSpotifyPreview = () => {
    return enrichmentType === 'spotify' && entityType === 'artist';
  };

  const handleLoadSpotifyData = async () => {
    const spotifyId = formData?.spotify_id;
    if (!spotifyId || !token) {
      setError('Please enter a Spotify ID');
      return;
    }

    try {
      setLoadingSpotify(true);
      setError('');
      const endpoint = `/admin/${entityType}s/${entityId}/spotify/fetch?spotify_id=${encodeURIComponent(spotifyId)}`;
      const response = await apiRequest<any>(endpoint, token);
      
      // Update form data with fetched Spotify data
      // Keep genres as array for storage, but display will handle conversion
      const updatedData = {
        ...formData,
        spotify_id: response.spotify_id || spotifyId,
        genres: Array.isArray(response.genres) ? response.genres : (response.genres ? [response.genres] : []),
        popularity: response.popularity || 0,
        images: response.images || [],
        match_confidence: response.match_confidence || 'manual',
      };
      
      setFormData(updatedData);
      setIframeKey(prev => prev + 1); // Refresh iframe
    } catch (err: any) {
      setError(err.message || 'Failed to load Spotify data');
    } finally {
      setLoadingSpotify(false);
    }
  };

  const renderField = (label: string, path: string[], type: string = 'text', options?: { value: string; label: string }[], readOnly: boolean = false) => {
    const value = path.reduce((obj, key) => obj?.[key], formData);
    let displayValue = value === undefined || value === null ? '' : value;
    
    // Convert genres array to comma-separated string for display
    if (path.length === 1 && path[0] === 'genres' && Array.isArray(displayValue)) {
      displayValue = displayValue.join(', ');
    }
    
    return (
      <div className="form-group" key={path.join('.')}>
        <label>{label}</label>
        {type === 'textarea' ? (
          <textarea
            value={typeof displayValue === 'string' ? displayValue : JSON.stringify(displayValue, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                updateField(path, parsed);
              } catch {
                updateField(path, e.target.value);
              }
            }}
            rows={4}
          />
        ) : type === 'checkbox' ? (
          <input
            type="checkbox"
            checked={!!displayValue}
            onChange={(e) => updateField(path, e.target.checked)}
          />
        ) : type === 'select' && options ? (
          <select
            value={typeof displayValue === 'string' ? displayValue : ''}
            onChange={(e) => updateField(path, e.target.value)}
            disabled={readOnly}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              backgroundColor: readOnly ? '#f5f5f5' : 'white',
              cursor: readOnly ? 'not-allowed' : 'default'
            }}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={typeof displayValue === 'string' || typeof displayValue === 'number' ? displayValue : JSON.stringify(displayValue)}
            onChange={(e) => {
              if (!readOnly) {
                if (type === 'number') {
                  updateField(path, parseFloat(e.target.value) || 0);
                } else {
                  updateField(path, e.target.value);
                }
              }
            }}
            disabled={readOnly}
            readOnly={readOnly}
            style={{
              backgroundColor: readOnly ? '#f5f5f5' : 'white',
              color: readOnly ? '#666' : '#000',
              cursor: readOnly ? 'not-allowed' : 'text'
            }}
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content enrichment-modal" onClick={(e) => e.stopPropagation()}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  const title = `${enrichmentType === 'ticketmaster' ? 'Ticketmaster' : enrichmentType === 'etix' ? 'Etix' : enrichmentType === 'eventim' ? 'EventIM' : 'Spotify'} Enrichment Data`;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div 
        className="modal-content enrichment-modal" 
        style={shouldShowSpotifyPreview() ? { maxWidth: '1600px', width: '95vw' } : {}}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{title}</h3>
        {error && <div className="error-banner">{error}</div>}
        
        {showUnsavedWarning && (
          <div className="unsaved-warning">
            <p>You have unsaved changes. Are you sure you want to close?</p>
            <div className="warning-actions">
              <button onClick={() => setShowUnsavedWarning(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleConfirmClose} className="btn-danger">
                Close Without Saving
              </button>
            </div>
          </div>
        )}

        <div className={`enrichment-content ${shouldShowSpotifyPreview() ? 'enrichment-content-with-preview' : ''}`}>
          <div className="enrichment-form">
          {enrichmentType === 'ticketmaster' && entityType === 'venue' && (
            <>
              {renderField('Ticketmaster ID', ['ticketmaster_id'])}
              {renderField('Address Line 1', ['address', 'line1'])}
              {renderField('Address Line 2', ['address', 'line2'])}
              {renderField('City', ['address', 'city'])}
              {renderField('State', ['address', 'state'])}
              {renderField('State Code', ['address', 'state_code'])}
              {renderField('Postal Code', ['address', 'postal_code'])}
              {renderField('Country', ['address', 'country'])}
              {renderField('Timezone', ['address', 'timezone'])}
              {renderField('Is Active', ['is_active'], 'checkbox')}
              {renderField('Notes', ['notes'], 'textarea')}
            </>
          )}
          
          {enrichmentType === 'ticketmaster' && entityType === 'event' && (
            <>
              {renderField('Ticketmaster ID', ['ticketmaster_id'])}
              {renderField('URL', ['url'])}
              {renderField('Image URL', ['image_url'])}
              {renderField('Primary Genre', ['primary_genre'])}
              {renderField('Public Sale Start', ['public_sale_start'])}
              {renderField('Public Sale End', ['public_sale_end'])}
              {renderField('Pricing (JSON)', ['pricing'], 'textarea')}
              {renderField('Classifications (JSON)', ['classifications'], 'textarea')}
            </>
          )}
          
          {enrichmentType === 'ticketmaster' && entityType === 'artist' && (
            <>
              {renderField('Ticketmaster IDs (comma-separated)', ['ticketmaster_ids'])}
              {renderField('URL', ['url'])}
              {renderField('Locale', ['locale'])}
              {renderField('Image URL', ['image_url'])}
              {renderField('External Links (JSON)', ['external_links'], 'textarea')}
            </>
          )}
          
          {enrichmentType === 'spotify' && entityType === 'artist' && (
            <>
              {entityName && (
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Artist Name</label>
                  <input
                    type="text"
                    value={entityName}
                    disabled
                    readOnly
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed'
                    }}
                  />
                </div>
              )}
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Spotify ID</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={formData?.spotify_id || ''}
                    onChange={(e) => updateField(['spotify_id'], e.target.value)}
                    style={{ flex: 1 }}
                    placeholder="Enter Spotify Artist ID"
                  />
                  <button
                    type="button"
                    onClick={handleLoadSpotifyData}
                    disabled={loadingSpotify || !formData?.spotify_id}
                    className="btn-primary"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {loadingSpotify ? 'Loading...' : 'Load'}
                  </button>
                </div>
              </div>
              {renderField('Genres (comma-separated)', ['genres'], 'text', undefined, true)}
              {renderField('Popularity', ['popularity'], 'number', undefined, true)}
              {renderField('Match Confidence', ['match_confidence'], 'select', [
                { value: 'exact', label: 'Exact' },
                { value: 'fuzzy', label: 'Fuzzy' },
                { value: 'low', label: 'Low' },
                { value: 'manual', label: 'Manual' },
                { value: 'none', label: 'None' }
              ], true)}
              {renderField('Searched Name', ['searched_name'], 'text', undefined, true)}
              {renderField('Images (JSON)', ['images'], 'textarea', undefined, true)}
            </>
          )}

          {enrichmentType === 'etix' && entityType === 'venue' && (
            <>
              {renderField('Etix ID', ['etix_id'])}
              {renderField('Address Line 1', ['address1'])}
              {renderField('City', ['city'])}
              {renderField('State', ['state'])}
              {renderField('Postal Code', ['postal_code'])}
              {renderField('Latitude', ['latitude'])}
              {renderField('Longitude', ['longitude'])}
              {renderField('Timezone', ['timezone'])}
            </>
          )}

          {enrichmentType === 'etix' && entityType === 'event' && (
            <>
              {renderField('Etix ID', ['etix_id'])}
              {renderField('Purchase URL', ['purchase_url'])}
              {renderField('Category', ['category'])}
              {renderField('Subcategory', ['subcategory'])}
              {renderField('Status', ['status'])}
              {renderField('Last Seen At', ['last_seen_at'])}
              {renderField('Seen Count', ['seen_count'], 'number')}
              {renderField('Update Count', ['update_count'], 'number')}
            </>
          )}

          {enrichmentType === 'etix' && entityType === 'artist' && (
            <>
              {renderField('Etix ID', ['etix_id'])}
              {renderField('Role', ['role'])}
              {renderField('Social Links (JSON)', ['social_links'], 'textarea')}
            </>
          )}

          {enrichmentType === 'eventim' && entityType === 'venue' && (
            <>
              {renderField('EventIM ID', ['eventim_id'])}
              {renderField('Address Line 1', ['address1'])}
              {renderField('City', ['city'])}
              {renderField('State', ['state'])}
              {renderField('Postal Code', ['postal_code'])}
              {renderField('Latitude', ['latitude'])}
              {renderField('Longitude', ['longitude'])}
              {renderField('Room', ['room'])}
              {renderField('Capacity', ['capacity'], 'number')}
            </>
          )}

          {enrichmentType === 'eventim' && entityType === 'event' && (
            <>
              {renderField('EventIM ID', ['eventim_id'])}
              {renderField('Purchase URL', ['purchase_url'])}
              {renderField('Image URL', ['image_url'])}
              {renderField('Genre', ['genre'])}
              {renderField('Tags (comma-separated)', ['tags'])}
              {renderField('Pricing (display)', ['pricing'])}
              {renderField('Price Min', ['price_min'], 'number')}
              {renderField('Price Max', ['price_max'], 'number')}
              {renderField('Price Currency', ['price_currency'])}
              {renderField('Last Seen At', ['last_seen_at'])}
              {renderField('Seen Count', ['seen_count'], 'number')}
              {renderField('Update Count', ['update_count'], 'number')}
            </>
          )}

          {enrichmentType === 'eventim' && entityType === 'artist' && (
            <>
              {renderField('MusicBrainz ID', ['music_brainz_id'])}
            </>
          )}
          </div>

          {shouldShowSpotifyPreview() && (
            <div className="spotify-preview-panel">
              <div className="spotify-preview-header">
                <h4>Spotify Preview</h4>
                <button 
                  type="button" 
                  onClick={handleRefreshIframe}
                  className="btn-sm btn-secondary"
                  title="Refresh preview with current field values"
                >
                  Refresh
                </button>
              </div>
              <div className="spotify-preview-container">
                {getSpotifyId() ? (
                  <iframe
                    key={iframeKey}
                    style={{ borderRadius: '12px', border: 'none' }}
                    src={`https://open.spotify.com/embed/artist/${getSpotifyId()}?utm_source=stagespotter`}
                    width="100%"
                    height="352"
                    frameBorder="0"
                    allowFullScreen
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    title={`Spotify preview for artist ${getSpotifyId()}`}
                  />
                ) : (
                  <div className="spotify-preview-placeholder">
                    <p>Enter a Spotify ID to preview</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleClose}>
            Close
          </button>
          <button type="button" onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnrichmentEditor;

